import { useQuery } from 'react-query'
import { useRecoilValue } from 'recoil'

import { APIs } from '@/shared/api/index'
import { CMD_TYPE } from '@/shared/constants/common'
import useAPI from '@/shared/hooks/useAPI.hook'
import useCookies from '@/shared/hooks/useCookies.hook'
import { homeData } from '@/shared/recoil/common/atom'
import { otpJoinTermsList, otpTelecom, otpTermsAgrees } from '@/shared/recoil/otp/atom'
import { captureHomeTermsError } from '@/shared/utils/sentry/validation'

export const useGetFAQs = (group: GroupType) => {
  const data: RequestFaqListBody = {
    Group: group,
    Length: 100,
    Offset: 0,
  }
  return useQuery(
    CMD_TYPE.GET_FAQ_LIST,
    async () => await APIs.api.requestSRS<FAQ[]>(CMD_TYPE.GET_FAQ_LIST, data),
    {
      staleTime: 10 * 1000,
    },
  )
}

export const useGetNotices = () => {
  const data: RequestNoticeBody = {
    Length: 100,
    Offset: 0,
  }
  return useQuery(
    CMD_TYPE.GET_NOTICE_LIST,
    async () => await APIs.api.requestSRS<Notice[]>(CMD_TYPE.GET_NOTICE_LIST, data),
    {
      staleTime: 10 * 1000,
    },
  )
}
export const useGetNoticeDetail = (noticeIdx: string) => {
  const api = useAPI()
  const data: RequestNoticeDetailBody = {
    Idx: noticeIdx,
  }
  return useQuery(
    CMD_TYPE.GET_NOTICE_DETAIL,
    async () => await api.requestSRS<Notice>(CMD_TYPE.GET_NOTICE_DETAIL, data),
    {
      staleTime: 10 * 1000,
    },
  )
}
export const useGetNews = () => {
  const data: RequestNewsBody = {
    Length: 100,
    Offset: 0,
  }
  return useQuery(
    CMD_TYPE.GET_NEWS_LIST,
    async () => await APIs.api.requestSRS<News[]>(CMD_TYPE.GET_NEWS_LIST, data),
    {
      staleTime: 10 * 1000,
    },
  )
}

export const useGetHomeTerms = () => {
  const data: HttpBody = {}
  return useQuery(
    CMD_TYPE.GET_HOME_TERMS,
    async () => {
      const result = await APIs.api.requestSRS<TermsResponse<HomeTerms>>(
        CMD_TYPE.GET_HOME_TERMS,
        data,
      )
      if (result) captureHomeTermsError(result)
      return result
    },

    {
      // staleTime: 10 * 1000,
    },
  )
}

export const useGetJoinTerms = () => {
  const data: HttpBody = {}

  const setOrder = (terms: JoinTerms) =>
    ({
      ...terms,
      Terms: terms.Terms.map((detail, index) => ({
        ...detail,
        Required: detail.Required ?? 0,
        Order: Number(terms.TermsOrder.charAt(index)),
      })),
    }) as JoinTerms

  return useQuery(CMD_TYPE.GET_JOIN_TERMS, async () => {
    const results = await APIs.api.requestSRS<TermsResponse<JoinTerms>>(
      CMD_TYPE.GET_JOIN_TERMS,
      data,
    )
    if (results) {
      return [setOrder(results.SKTTerms), setOrder(results.KTTerms), setOrder(results.LGUPTerms)]
    }
    return [
      {
        TermsIdx: 0,
        Terms: [],
        TermsOrder: '',
      },
      {
        TermsIdx: 0,
        Terms: [],
        TermsOrder: '',
      },
      {
        TermsIdx: 0,
        Terms: [],
        TermsOrder: '',
      },
    ]
  })
}

export const useSelectHomeTerms = (
  data: TermsResponse | undefined,
  telecom: string | undefined,
  termsIdx: string | undefined,
): string => {
  const termsData: HomeTerms[] | undefined =
    data != null ? [data.SKTTerms, data.KTTerms, data.LGUPTerms] : undefined

  const terms = (telecom: string | undefined, termsIdx: string | undefined): string => {
    try {
      return termsData ? termsData[Number(telecom)].Terms[Number(termsIdx) - 1].Contents : ''
    } catch (error) {
      console.log(error)
      return ''
    }
  }
  return terms(telecom, termsIdx)
}

export const useAllRequiredTermsAgree = () => {
  const telecom = useRecoilValue(otpTelecom)
  const joinTermsList = useRecoilValue(otpJoinTermsList)
  const termsAgrees = useRecoilValue(otpTermsAgrees)

  return (
    joinTermsList?.[Number(telecom)]?.Terms.filter(
      (terms) => terms.Required === 1 && terms.Order !== undefined,
    ).every(({ Order }) => termsAgrees[Order! - 1] === 1) ?? false
  )
}

export const useGetHomeDatas = () => {
  const datas = useRecoilValue(homeData)
  const data: HttpBody = {}
  const result = useQuery(
    CMD_TYPE.GET_HOME_DATA,
    async () => await APIs.api.requestSRS<ResponseHomeBody>(CMD_TYPE.GET_HOME_DATA, data),
  )
  function news() {
    if (datas != null) {
      return datas.News
    }
  }
  function notice() {
    if (datas != null) {
      return datas.Notice
    }
  }
  function popup() {
    if (datas != null) {
      return datas.Popup
    }
  }
  return {
    query: result,
    news,
    notice,
    popup,
  }
}

export const useLoginData = () => {
  const cookie = useCookies()
  const { PNumber, SessionID } = cookie.getSessionCookie()
  // "console.log()
  // cookie.login(PNumber, result.)

  const data: RequestSessionCheckBody = {
    PNumber,
    SessionID,
  }
  return useQuery(
    CMD_TYPE.SESSION_CHECK,
    async () => {
      const result = await APIs.api.requestSRS<ResponseLoginBody>(CMD_TYPE.SESSION_CHECK, data)
      if (result?.SessionID) {
        cookie.login(result.PNumber, result.SessionID, String(result.Telecom))
      }
      return result
    },
    {
      staleTime: 10 * 1000,
    },
  )
}

export const useStickerData = (sessionId?: string) => {
  const cookie = useCookies()

  return useQuery(
    [CMD_TYPE.STICKER_HISTORY_INQUIRY, sessionId],
    async () => {
      const { PNumber, SessionID } = cookie.getSessionCookie()
      return await APIs.api.requestSRS<ResponseStickerHistoryInquiryBody>(
        CMD_TYPE.STICKER_HISTORY_INQUIRY,
        { PNumber, SessionID },
      )
    },
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
    },
  )
}
